import { useState, useEffect } from "react";
import PhoneFrame, { screen } from "./PhoneFrame";

interface Message {
  from: "user" | "coach";
  text: string;
}

const conversation: Message[] = [
  { from: "user", text: "Slept awful last night, maybe 5 hours" },
  {
    from: "coach",
    text: "I saw — your HRV is down 18% and readiness is at 52. Let's swap today's intervals for steady Zone 2. Keep HR under 145.",
  },
  { from: "user", text: "Fine. What do I eat before it?" },
  {
    from: "coach",
    text: "Keep it light on low sleep:\n— banana + peanut butter\n— coffee, 30 min out\n— 500ml water + electrolytes",
  },
];

const DELAY_BETWEEN = 2200;
const TYPING_DURATION = 1200;
const RESTART_PAUSE = 4500;

export default function CoachChatMock() {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);

  useEffect(() => {
    if (visibleCount >= conversation.length) {
      const t = setTimeout(() => setVisibleCount(0), RESTART_PAUSE);
      return () => clearTimeout(t);
    }
    const next = conversation[visibleCount];
    if (next.from === "coach") {
      const t1 = setTimeout(() => setShowTyping(true), DELAY_BETWEEN * 0.3);
      const t2 = setTimeout(() => {
        setShowTyping(false);
        setVisibleCount((c) => c + 1);
      }, DELAY_BETWEEN * 0.3 + TYPING_DURATION);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    const t = setTimeout(
      () => setVisibleCount((c) => c + 1),
      visibleCount === 0 ? 900 : DELAY_BETWEEN
    );
    return () => clearTimeout(t);
  }, [visibleCount]);

  return (
    <PhoneFrame caption="In-app coach — live from your data">
      {/* Header */}
      <div
        className="flex items-center justify-between pb-3 mb-3"
        style={{ borderBottom: `1px solid ${screen.line}` }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: screen.ink }}>
            Coach
          </div>
          <div style={{ ...screen.label, marginTop: 3 }}>
            Reads: Garmin · Sleep · HRV · Log
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 99,
              background: screen.signal,
            }}
          />
          <span style={{ ...screen.label, color: screen.signal }}>Live</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex flex-col gap-2" style={{ minHeight: 360 }}>
        {conversation.slice(0, visibleCount).map((msg, i) => (
          <div
            key={i}
            className="mk-rise flex"
            style={{
              justifyContent: msg.from === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              style={{
                maxWidth: "84%",
                padding: "9px 12px",
                borderRadius:
                  msg.from === "user" ? "12px 3px 12px 12px" : "3px 12px 12px 12px",
                background: msg.from === "user" ? screen.signal : "#14171a",
                border:
                  msg.from === "user" ? "none" : `1px solid ${screen.line}`,
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  whiteSpace: "pre-line",
                  color: msg.from === "user" ? "#0a0b0c" : "#c8d0d4",
                  fontWeight: msg.from === "user" ? 500 : 400,
                }}
              >
                {msg.text}
              </div>
            </div>
          </div>
        ))}

        {showTyping && (
          <div className="mk-rise flex justify-start">
            <div
              className="flex items-center gap-1"
              style={{
                padding: "11px 14px",
                borderRadius: "3px 12px 12px 12px",
                background: "#14171a",
                border: `1px solid ${screen.line}`,
              }}
            >
              {[0, 1, 2].map((d) => (
                <span
                  key={d}
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 99,
                    background: screen.signal,
                    animation: `mk-blink 1.2s steps(1) infinite`,
                    animationDelay: `${d * 0.2}s`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div
        className="flex items-center gap-2 mt-3 pt-3"
        style={{ borderTop: `1px solid ${screen.line}` }}
      >
        <div
          className="flex-1"
          style={{
            background: "#14171a",
            border: `1px solid ${screen.line}`,
            borderRadius: 8,
            padding: "9px 12px",
            fontSize: 12,
            color: screen.dim,
          }}
        >
          Message your coach
        </div>
        <div
          className="flex items-center justify-center"
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            background: screen.signal,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0a0b0c" strokeWidth="2.5">
            <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="square" />
          </svg>
        </div>
      </div>
    </PhoneFrame>
  );
}
